import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	HEADER_PAD_CLASS,
	NUMBER_CELL_CLASS,
	TEXT_CELL_CLASS,
	ONE_LINE_CELL_CLASS,
} from "@/controls/ag-grid/OneLineCells";
import type { MillSetupGridRow } from "../models/ProductMillSetup";

/** Width reserved for the expand control on every stand row, in pixels. */
export const CONTROL_SLOT = 18;

export interface MillSetupGridContext {
	expandedStands: Set<number>;
	onToggleExpandStand: (standId: number) => void;
}

/** Stand name; a stand with alternative passes shows how many and expands them, like Rolls Cartridge. */
const StandCellRenderer = (
	params: ICellRendererParams<MillSetupGridRow> & {
		context?: MillSetupGridContext;
	},
) => {
	const row = params.data;
	const ctx = params.context;
	if (!row) return null;

	const standId = row.StandId;
	const hasAlternatives = !row.IsAlternative && row.AlternativesCount > 0;
	const expanded =
		standId != null && ctx ? ctx.expandedStands.has(standId) : false;

	const handleToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (standId != null && ctx) ctx.onToggleExpandStand(standId);
	};

	const name = (
		<Box
			className={hasAlternatives ? "mill-setup-toggle" : undefined}
			onClick={hasAlternatives ? handleToggle : undefined}
			sx={{
				display: "inline-flex",
				alignItems: "center",
				minWidth: 0,
				cursor: hasAlternatives ? "pointer" : undefined,
			}}
		>
			<Box
				sx={{
					width: CONTROL_SLOT,
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
				}}
			>
				{hasAlternatives &&
					(expanded ? (
						<ExpandMoreIcon
							sx={{ fontSize: CONTROL_SLOT, color: "primary.main" }}
						/>
					) : (
						<ChevronRightIcon
							sx={{ fontSize: CONTROL_SLOT, color: "primary.main" }}
						/>
					))}
			</Box>
			<Typography
				component="span"
				sx={{
					fontSize: 13,
					fontWeight: hasAlternatives ? 700 : undefined,
					color: "inherit",
				}}
			>
				{row.StandName}
			</Typography>
			{hasAlternatives && (
				<Typography
					component="span"
					sx={{ fontSize: 11, color: "text.secondary", ml: 0.5 }}
				>
					({row.AlternativesCount + 1})
				</Typography>
			)}
		</Box>
	);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				width: "100%",
				overflow: "hidden",
			}}
		>
			{hasAlternatives ? (
				<Tooltip
					title={`This stand has ${row.AlternativesCount + 1} passes. Click to ${
						expanded ? "hide" : "show"
					} the alternatives.`}
					arrow
					placement="right"
				>
					{name}
				</Tooltip>
			) : (
				name
			)}
		</Box>
	);
};

interface UseMillSetupTableDataProps {
	onDelete: (row: MillSetupGridRow) => void;
}

const formatNumber = (params: { value: number | null }) =>
	params.value != null ? params.value.toFixed(3) : "";

export const useMillSetupTableData = ({
	onDelete,
}: UseMillSetupTableDataProps) => {
	const columnDefs: ColDef<MillSetupGridRow>[] = [
		{
			field: "StandName",
			headerName: "Stand",
			flex: 0.7,
			minWidth: 75,
			cellRenderer: StandCellRenderer,
			headerClass: HEADER_PAD_CLASS,
		},
		{
			field: "PassIdName",
			headerName: "Pass ID",
			flex: 1.4,
			minWidth: 95,
			tooltipField: "PassIdName",
			headerClass: HEADER_PAD_CLASS,
		},
		{
			field: "PassDepth",
			headerName: "Pass Depth",
			flex: 0.6,
			minWidth: 62,
			cellClass: [ONE_LINE_CELL_CLASS, NUMBER_CELL_CLASS],
			headerClass: HEADER_PAD_CLASS,
			valueFormatter: formatNumber,
		},
		{
			field: "PassWidth",
			headerName: "Pass Width",
			flex: 0.6,
			minWidth: 62,
			cellClass: [ONE_LINE_CELL_CLASS, NUMBER_CELL_CLASS],
			headerClass: HEADER_PAD_CLASS,
			valueFormatter: formatNumber,
		},
		{
			field: "BarSize",
			headerName: "Bar Size",
			flex: 1,
			minWidth: 70,
			headerClass: HEADER_PAD_CLASS,
		},
		{
			field: "RollPart",
			headerName: "Roll Part",
			flex: 0.6,
			minWidth: 58,
			cellClass: [ONE_LINE_CELL_CLASS, NUMBER_CELL_CLASS],
			headerClass: HEADER_PAD_CLASS,
			valueFormatter: formatNumber,
		},
		{
			field: "RolledAl",
			headerName: "Rolled Al",
			flex: 0.6,
			minWidth: 58,
			headerClass: HEADER_PAD_CLASS,
		},
		{
			field: "Comments",
			headerName: "Comments",
			flex: 1.6,
			minWidth: 80,
			tooltipField: "Comments",
			cellClass: [ONE_LINE_CELL_CLASS, TEXT_CELL_CLASS],
			headerClass: HEADER_PAD_CLASS,
		},
		{
			headerName: "",
			width: 60,
			minWidth: 60,
			maxWidth: 60,
			sortable: false,
			filter: false,
			resizable: false,
			suppressSizeToFit: true,
			cellRenderer: (params: ICellRendererParams<MillSetupGridRow>) => {
				if (!params.data || params.data.IsEmpty || params.data.IsAlternative)
					return null;
				return (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<Tooltip title="Delete">
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									onDelete(params.data as MillSetupGridRow);
								}}
								color="error"
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				);
			},
		},
	];

	return { columnDefs };
};
